import * as React from 'react';
import styled, { ThemeContext } from 'styled-components';
import { JRCIconButton } from '../JRCIconButton/JRCIconButton';
import { JRCButton, JRCButtonProps } from 'components/JRCButton/JRCButton';
import { JRCTypography } from '../JRCTypography/JRCTypography';
import { JRCIcon } from '../JRCIcon/JRCIcon';
import { getColor, MessageType } from 'components/Common/util/getColor.util';

export interface JRCAlertProps extends React.ComponentPropsWithoutRef<'div'> {
    /** Alert title */
    label: string | React.ReactNode;
    /** Main content of the alert */
    description?: string | React.ReactNode;
    /** Define the type of alert */
    type?: MessageType;
    /** Add a loader which will make the alert disappear after the timeout */
    hasLoader?: boolean;
    /** Timeout */
    timeout?: number;
    /** Add buttons on the alert */
    buttons?: JRCButtonProps[];
}

const Alert = styled.div<{ description?: string | React.ReactNode; type: MessageType }>`
    width: 320px;
    border-radius: 8px;
    background: ${(props) => (props.description ? 'white' : props.theme.color[getColor(props.type)])};
    overflow: hidden;
    ${(props) => props.description && `box-shadow: 0 0 5px ${props.theme.color.grey2}`};
`;
const Container = styled.div<{ center: boolean }>`
    display: flex;
    padding: 8px;
    justify-content: space-between;
    ${(props) => props.center && 'align-items: center'};
    flex-direction: row;
`;
const Content = styled.div`
    width: 100%;
    margin-left: 8px;
`;
const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const icons = {
    success: 'icon-check-circle',
    error: 'icon-times-circle',
    warning: 'icon-exclamation-circle',
};

export const JRCAlert = ({
    label,
    description,
    hasLoader,
    buttons,
    type = 'success',
    timeout = 3000,
}: JRCAlertProps) => {
    const colors = React.useContext(ThemeContext).color;
    const [showAlert, setShowAlert] = React.useState(true);
    const closeAlert = () => setShowAlert(false);

    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            setShowAlert(false);
        }, timeout);
        return () => {
            clearTimeout(timeoutId);
        };
    }, [timeout]);

    return (
        (showAlert && (
            <Alert
                description={description}
                type={type}
                style={{ '--timeout': `${timeout}ms` } as React.CSSProperties}
                className="slide-right"
                role="alert"
                aria-live="polite">
                <Container center={!description}>
                    <JRCIcon name={icons[type]} color={description ? getColor(type) : 'white'} />
                    <Content>
                        <Header>
                            <JRCTypography
                                size={description ? 'm' : 'l'}
                                weight="medium"
                                color={(!description && 'white') || undefined}>
                                {label}
                            </JRCTypography>
                            <JRCIconButton
                                size="small"
                                icon="icon-times"
                                onClick={closeAlert}
                                aria-label="close"
                                color={(!description && 'white') || undefined}
                            />
                        </Header>
                        {description && <JRCTypography>{description}</JRCTypography>}
                        {buttons &&
                            buttons.map((button: JRCButtonProps, index: number) => (
                                <JRCButton
                                    key={index}
                                    {...button}
                                    float="right"
                                    onClick={(e) => {
                                        if (button.onClick) button.onClick(e);
                                        closeAlert();
                                    }}>
                                    {button.children}
                                </JRCButton>
                            ))}
                    </Content>
                </Container>
                {hasLoader && description && <ProgressBar color={description ? colors[getColor(type)] : 'white'} />}
            </Alert>
        )) ||
        null
    );
};

/**
 * @member color color of the bar
 */
interface ProgressBarProps {
    color: string;
}

const Bar = styled.div<ProgressBarProps>`
    height: 4px;
    border-radius: 2px;
    background-color: ${(props) => props.color};
`;

const ProgressBar = ({ color }: ProgressBarProps) => {
    return <Bar color={color} className="scale-left" />;
};
